import React, { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { 
  BarChart3, TrendingUp, TrendingDown, Minus, Target, Award,
  Activity, Shield, Heart, RefreshCw, ArrowUp, ArrowDown
} from 'lucide-react';
import { PageHeader, MetricCard, SectionHeader, ModernCard, modernTabsClass, modernTabTriggerClass } from '@/components/ModernUI';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Benchmarking() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  
  const fetchData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/benchmarks/compare`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        setData(await response.json());
      } else {
        toast.error('Failed to load benchmark data');
      }
    } catch (error) {
      toast.error('Error loading benchmarks');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchData();
  }, [token]);
  
  const getPercentileColor = (percentile) => {
    if (percentile >= 75) return 'text-emerald-600';
    if (percentile >= 50) return 'text-teal-600';
    if (percentile >= 25) return 'text-amber-600';
    return 'text-red-600';
  };
  
  const getPercentileBg = (percentile) => {
    if (percentile >= 75) return 'bg-emerald-500';
    if (percentile >= 50) return 'bg-teal-500';
    if (percentile >= 25) return 'bg-amber-500';
    return 'bg-red-500';
  };
  
  const getStatusBadge = (status, lowerIsBetter) => {
    const isGood = status === 'above_average';
    return (
      <Badge variant="outline" className={
        isGood 
          ? "bg-emerald-50 text-emerald-700 border-emerald-200" 
          : "bg-amber-50 text-amber-700 border-amber-200"
      }>
        {isGood ? (
          <><TrendingUp className="h-3 w-3 mr-1" /> Above Avg</>
        ) : (
          <><TrendingDown className="h-3 w-3 mr-1" /> Below Avg</>
        )}
      </Badge>
    );
  };
  
  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Clinical Quality': return <Activity className="h-5 w-5 text-blue-500" />;
      case 'Safety': return <Shield className="h-5 w-5 text-red-500" />;
      case 'Patient Experience': return <Heart className="h-5 w-5 text-pink-500" />;
      default: return <BarChart3 className="h-5 w-5 text-gray-500" />;
    }
  };
  
  const groupByCategory = (comparisons) => {
    const grouped = {};
    comparisons?.forEach(c => {
      if (!grouped[c.category]) grouped[c.category] = [];
      grouped[c.category].push(c);
    });
    return grouped;
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin h-8 w-8 border-4 border-teal-500 border-t-transparent rounded-full" />
      </div>
    );
  }
  
  const grouped = groupByCategory(data?.comparisons);
  
  return (
    <div className="space-y-6" data-testid="benchmarking-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold font-[Manrope] bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent flex items-center gap-2">
            <BarChart3 className="h-7 w-7 text-teal-500" />
            National Benchmarking
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Compare your hospice against national averages and industry standards
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>
      
      {/* Overall Score Card */}
      <Card className="border-teal-200 bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-900/10 dark:to-cyan-900/10">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="flex-shrink-0">
              <div className={`relative w-32 h-32 rounded-full border-8 ${getPercentileBg(data?.overall_percentile)} border-opacity-20 flex items-center justify-center`}>
                <div className="text-center">
                  <p className={`text-4xl font-bold ${getPercentileColor(data?.overall_percentile)}`}>
                    {data?.overall_percentile || 0}
                  </p>
                  <p className="text-xs text-muted-foreground">Percentile</p>
                </div>
              </div>
            </div>
            <div className="flex-1 text-center md:text-left">
              <h2 className="text-xl font-bold mb-2">Overall Performance</h2>
              <p className="text-muted-foreground mb-4">{data?.summary}</p>
              <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                <div className="text-center">
                  <p className="text-2xl font-bold text-emerald-600">{data?.metrics_above_average}</p>
                  <p className="text-xs text-muted-foreground">Above Average</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-amber-600">{(data?.total_metrics || 0) - (data?.metrics_above_average || 0)}</p>
                  <p className="text-xs text-muted-foreground">Below Average</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-teal-600">{data?.total_metrics}</p>
                  <p className="text-xs text-muted-foreground">Total Metrics</p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Metrics by Category */}
      <Tabs defaultValue={Object.keys(grouped)[0] || 'Clinical Quality'} className="space-y-4">
        <TabsList className="flex flex-wrap h-auto gap-1">
          {Object.keys(grouped).map(category => (
            <TabsTrigger key={category} value={category} className="gap-2">
              {getCategoryIcon(category)}
              <span className="hidden sm:inline">{category}</span>
            </TabsTrigger>
          ))}
        </TabsList>
        
        {Object.entries(grouped).map(([category, metrics]) => (
          <TabsContent key={category} value={category}>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {getCategoryIcon(category)}
                  {category}
                </CardTitle>
                <CardDescription>
                  {metrics.filter(m => m.status === 'above_average').length} of {metrics.length} metrics above national average
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {metrics.map((metric, idx) => (
                    <div key={idx} className="space-y-2">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{metric.metric}</span>
                          {getStatusBadge(metric.status, metric.lower_is_better)}
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-muted-foreground">
                            National Avg: <span className="font-medium">{metric.national_avg}{metric.unit}</span>
                          </span>
                          <span className={`font-bold ${getPercentileColor(metric.percentile)}`}>
                            Your Value: {metric.your_value}{metric.unit}
                          </span>
                        </div>
                      </div>
                      
                      {/* Visual comparison bar */}
                      <div className="relative h-8 bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden">
                        {/* Bottom quartile zone */}
                        <div 
                          className="absolute h-full bg-red-100 dark:bg-red-900/30"
                          style={{ 
                            left: 0, 
                            width: `${(metric.bottom_quartile / Math.max(metric.top_quartile * 1.2, metric.your_value * 1.2)) * 100}%` 
                          }}
                        />
                        {/* Average zone */}
                        <div 
                          className="absolute h-full bg-amber-100 dark:bg-amber-900/30"
                          style={{ 
                            left: `${(metric.bottom_quartile / Math.max(metric.top_quartile * 1.2, metric.your_value * 1.2)) * 100}%`,
                            width: `${((metric.national_avg - metric.bottom_quartile) / Math.max(metric.top_quartile * 1.2, metric.your_value * 1.2)) * 100}%`
                          }}
                        />
                        {/* Above average zone */}
                        <div 
                          className="absolute h-full bg-emerald-100 dark:bg-emerald-900/30"
                          style={{ 
                            left: `${(metric.national_avg / Math.max(metric.top_quartile * 1.2, metric.your_value * 1.2)) * 100}%`,
                            width: `${((metric.top_quartile - metric.national_avg) / Math.max(metric.top_quartile * 1.2, metric.your_value * 1.2)) * 100}%`
                          }}
                        />
                        
                        {/* Your value marker */}
                        <div 
                          className={`absolute top-0 h-full w-1 ${getPercentileBg(metric.percentile)}`}
                          style={{ 
                            left: `${(metric.your_value / Math.max(metric.top_quartile * 1.2, metric.your_value * 1.2)) * 100}%`
                          }}
                        >
                          <div className={`absolute -top-1 -left-3 w-7 h-7 rounded-full ${getPercentileBg(metric.percentile)} flex items-center justify-center shadow-lg`}>
                            <span className="text-white text-xs font-bold">You</span>
                          </div>
                        </div>
                        
                        {/* Legend */}
                        <div className="absolute bottom-0 left-2 right-2 flex justify-between text-[10px] text-muted-foreground">
                          <span>Bottom 25%</span>
                          <span>National Avg</span>
                          <span>Top 25%</span>
                        </div>
                      </div>
                      
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>Percentile: <span className={`font-bold ${getPercentileColor(metric.percentile)}`}>{metric.percentile}th</span></span>
                        <span className="flex items-center gap-1">
                          {metric.lower_is_better ? (
                            <><ArrowDown className="h-3 w-3" /> Lower is better</>
                          ) : (
                            <><ArrowUp className="h-3 w-3" /> Higher is better</>
                          )}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
      
      {/* Data Source Note */}
      <Card className="bg-gray-50 dark:bg-gray-900/50">
        <CardContent className="p-4">
          <p className="text-xs text-muted-foreground text-center">
            Benchmark data based on NHPCO (National Hospice and Palliative Care Organization) and CMS quality measures. 
            Percentiles are calculated using industry quartile ranges.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
